import { useLingui } from '@lingui/react/macro';
import {
  createFileRoute,
  type SearchSchemaInput,
} from '@tanstack/react-router';
import { getQueryKey } from '@trpc/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { Input, Select, SelectItem } from '@utima/ui';
import { useCallback, useState } from 'react';

import type { DataTableQuery } from '@/components/dataTable/DataTable';
import { TablePage } from '@/components/page/TablePage';
import { MergeAttributeDialog } from '@/features/feeds/MergeAttributeDialog';
import { RenameAttributeDialog } from '@/features/feeds/RenameAttributeDialog';
import {
  type FeedAttributeRow,
  useFeedAttributesCols,
} from '@/features/feeds/useFeedAttributesCols';
import { trpc } from '@/services/trpc';

type AttributesSearch = {
  page?: number;
  pageSize?: number;
  sort?: string;
  search?: string;
};

const ALL = '__all__';

type AttributeStatusFilter = 'active' | 'inactive' | 'unused';

// Mirrors the products route's toolbar shell so the two sibling screens keep
// the same height and rhythm.
const toolbarShellClassName =
  'mb-4 flex min-h-11 flex-wrap items-center gap-2 rounded-lg border border-transparent py-1.5 px-2';

export const Route = createFileRoute(
  '/_baseLayout/chats/$chatId/feeds/$feedId/attributes',
)({
  component: AttributesPage,
  loader: async ({ context: { trpcUtils }, params: { feedId, chatId } }) =>
    Promise.all([
      trpcUtils.feeds.findOne.ensureData({ id: feedId }),
      trpcUtils.chats.findOne.ensureData({ id: chatId }),
    ]),
  validateSearch: (
    search: AttributesSearch & SearchSchemaInput,
  ): AttributesSearch => ({
    // The DataTable's own params. They have to survive here or the table
    // loses pagination, sort and search on this route.
    ...(search?.page === undefined ? {} : { page: search.page }),
    ...(search?.pageSize === undefined ? {} : { pageSize: search.pageSize }),
    ...(search?.sort ? { sort: search.sort } : {}),
    ...(search?.search ? { search: search.search } : {}),
  }),
});

function AttributesPage() {
  const { chatId, feedId } = Route.useParams();
  const searchParams = Route.useSearch();
  const { t } = useLingui();

  const [chatData] = trpc.chats.findOne.useSuspenseQuery({ id: chatId });
  const [feedData] = trpc.feeds.findOne.useSuspenseQuery({ id: feedId });

  /**
   * A feed's vocabulary is tens to low hundreds of rows and every column is
   * either a stored field or an aggregate the endpoint already computed, so
   * the whole list is fetched once and paged in memory. Paging it server-side
   * would re-run the usage and category aggregations on every page turn for
   * no gain.
   */
  const { data: attributes, dataUpdatedAt } =
    trpc.shopProductAttributes.listByFeed.useQuery({ feedId });

  const [statusFilter, setStatusFilter] = useState<
    AttributeStatusFilter | undefined
  >();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const [renameTarget, setRenameTarget] = useState<FeedAttributeRow | null>(
    null,
  );
  const [mergeSource, setMergeSource] = useState<FeedAttributeRow | null>(null);

  const columns = useFeedAttributesCols({
    onRename: setRenameTarget,
    onMerge: setMergeSource,
  });

  const sortValue = searchParams?.sort;

  const handleQuery = useCallback<DataTableQuery<FeedAttributeRow>>(
    async ({ page, pageSize }) => {
      const rows = attributes ?? [];
      const term = debouncedSearch.trim().toLowerCase();

      const filtered = rows.filter(attribute => {
        if (statusFilter === 'active' && !attribute.active) {
          return false;
        }

        if (statusFilter === 'inactive' && attribute.active) {
          return false;
        }

        if (statusFilter === 'unused' && attribute.activeValuesCount > 0) {
          return false;
        }

        if (!term) {
          return true;
        }

        return [
          attribute.name,
          attribute.displayName ?? '',
          ...(attribute.aliases ?? []),
        ].some(candidate => candidate.toLowerCase().includes(term));
      });

      const [sortKey, sortDirection] = (sortValue ?? 'name:asc').split(':');
      const direction = sortDirection === 'desc' ? -1 : 1;

      const sorted = [...filtered].sort((left, right) => {
        if (sortKey === 'productsCount') {
          return (left.productsCount - right.productsCount) * direction;
        }

        return (
          (left.displayName ?? left.name).localeCompare(
            right.displayName ?? right.name,
          ) * direction
        );
      });

      const offset = (page - 1) * pageSize;

      return {
        items: sorted.slice(offset, offset + pageSize),
        pageCount: Math.max(1, Math.ceil(sorted.length / pageSize)),
      };
    },
    [attributes, debouncedSearch, sortValue, statusFilter],
  );

  return (
    <>
      <TablePage
        title={t`Feed attributes`}
        breadcrumbs={[
          {
            label: chatData.name,
            to: '/chats/$chatId/edit',
            params: { chatId },
          },
          {
            label: t`Product feeds`,
            to: '/chats/$chatId/feeds',
            params: { chatId },
          },
          {
            label: feedData.name,
            to: '/chats/$chatId/feeds/$feedId/products',
            params: { chatId, feedId },
          },
          {
            label: t`Attributes`,
            to: '/chats/$chatId/feeds/$feedId/attributes',
            params: { chatId, feedId },
          },
        ]}
        columns={columns}
        showSearch={false}
        queryKey={[
          ...getQueryKey(trpc.shopProductAttributes.listByFeed),
          { feedId, debouncedSearch, statusFilter, sortValue, dataUpdatedAt },
        ]}
        onQueryFn={handleQuery}
      >
        <p className='mb-3 text-sm text-muted-fg'>
          {t`The vocabulary the enrichment model reuses for this feed. Switching an attribute off here hides it across the whole catalog and tells the model not to extract it again — removing one wrong value on a single product is done in that product's detail instead.`}
        </p>

        <div className={toolbarShellClassName}>
          <div className='w-56 shrink-0'>
            <Input
              type='search'
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={t`Search names and aliases`}
              className='h-8'
            />
          </div>

          <div className='w-56 shrink-0'>
            <Select
              size='sm'
              value={statusFilter ?? ALL}
              onValueChange={value =>
                setStatusFilter(
                  value === ALL ? undefined : (value as AttributeStatusFilter),
                )
              }
              placeholder={t`Status`}
            >
              <SelectItem value={ALL}>{t`All attributes`}</SelectItem>
              <SelectItem value='active'>{t`On for this feed`}</SelectItem>
              <SelectItem value='inactive'>{t`Switched off`}</SelectItem>
              <SelectItem value='unused'>{t`No active values`}</SelectItem>
            </Select>
          </div>
        </div>
      </TablePage>

      <RenameAttributeDialog
        attribute={renameTarget}
        onClose={() => setRenameTarget(null)}
      />

      <MergeAttributeDialog
        attribute={mergeSource}
        attributes={attributes ?? []}
        onClose={() => setMergeSource(null)}
      />
    </>
  );
}
